import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';

interface UserRow {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: 'admin' | 'supervisor' | 'agent';
  active: boolean;
  skills: string[];
  state: string;
  voiplineExtension?: string | null;
  sipUser?: string | null;
  sipPasswordSet?: boolean;
}

type UserModal =
  | { type: 'create' }
  | { type: 'edit'; user: UserRow }
  | null;

const EMPTY_CREATE = {
  email: '', password: '', firstName: '', lastName: '',
  role: 'agent' as UserRow['role'],
  voiplineExtension: '',
  sipUser: '',
  sipPassword: '',
};

const ROLE_OPTIONS: UserRow['role'][] = ['agent', 'supervisor', 'admin'];

export function UserManagementSection() {
  const queryClient = useQueryClient();
  const [modal, setModal] = useState<UserModal>(null);
  const [createForm, setCreateForm] = useState(EMPTY_CREATE);
  const [editForm, setEditForm] = useState({
    firstName: '', lastName: '', role: 'agent' as UserRow['role'], active: true, password: '', voiplineExtension: '',
    sipUser: '', sipPassword: '',
  });
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const usersQuery = useQuery({
    queryKey: ['users'],
    queryFn: () => api.get<UserRow[]>('/users'),
  });

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ['users'] });
  }

  const createUser = useMutation({
    mutationFn: (data: typeof createForm) => api.post('/users', data),
    onSuccess: () => {
      invalidate();
      setModal(null);
      setCreateForm(EMPTY_CREATE);
      setErrorMsg(null);
    },
    onError: (err: unknown) =>
      setErrorMsg((err as { detail?: { error?: string } })?.detail?.error ?? 'Failed to create user'),
  });

  const editUser = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Omit<Partial<typeof editForm>, 'voiplineExtension' | 'sipUser' | 'sipPassword'> & { voiplineExtension?: string | null; sipUser?: string | null; sipPassword?: string | null } }) =>
      api.patch(`/users/${id}`, data),
    onSuccess: () => {
      invalidate();
      setModal(null);
      setErrorMsg(null);
    },
    onError: (err: unknown) =>
      setErrorMsg((err as { detail?: { error?: string } })?.detail?.error ?? 'Failed to update user'),
  });

  function openEdit(u: UserRow) {
    setEditForm({
      firstName: u.firstName,
      lastName: u.lastName,
      role: u.role,
      active: u.active,
      password: '',
      voiplineExtension: u.voiplineExtension || '',
      sipUser: u.sipUser || '',
      sipPassword: '',
    });
    setErrorMsg(null);
    setModal({ type: 'edit', user: u });
  }

  function closeModal() { setModal(null); setErrorMsg(null); }

  function submitEdit() {
    if (modal?.type !== 'edit') return;
    const data: Omit<Partial<typeof editForm>, 'voiplineExtension' | 'sipUser' | 'sipPassword'> & { voiplineExtension?: string | null; sipUser?: string | null; sipPassword?: string | null } = {
      firstName: editForm.firstName,
      lastName: editForm.lastName,
      role: editForm.role,
      active: editForm.active,
      voiplineExtension: editForm.voiplineExtension || null,
      sipUser: editForm.sipUser || null,
    };
    if (editForm.password) data.password = editForm.password;
    // Only send sipPassword when the admin actually typed one. Empty string
    // would clobber the stored cred; null would also clear it. Skipping the
    // key entirely keeps the existing password.
    if (editForm.sipPassword) data.sipPassword = editForm.sipPassword;
    editUser.mutate({ id: modal.user.id, data });
  }

  function toggleActive(u: UserRow) {
    editUser.mutate({ id: u.id, data: { active: !u.active } });
  }

  return (
    <section className="card overflow-hidden">
      <header className="flex items-center justify-between px-4 py-3 border-b border-ink-200">
        <h2 className="text-sm font-semibold text-ink-900">Users</h2>
        <button
          className="btn-primary text-xs"
          onClick={() => { setCreateForm(EMPTY_CREATE); setErrorMsg(null); setModal({ type: 'create' }); }}
        >
          Add user
        </button>
      </header>

      {usersQuery.isLoading && <p className="px-4 py-3 text-sm text-ink-400">Loading…</p>}

      {usersQuery.data && (
        <div className="overflow-x-auto">
        <table className="w-full text-sm whitespace-nowrap">
          <thead className="bg-ink-50">
            <tr>
              {['Name', 'Email', 'Role', 'Status', 'VoIP Ext', 'SIP', 'Skills', ''].map((h) => (
                <th key={h} className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-ink-500">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {usersQuery.data.map((u) => (
              <tr key={u.id} className="border-t border-ink-100 hover:bg-ink-50">
                <td className="px-4 py-2 font-medium">{u.firstName} {u.lastName}</td>
                <td className="px-4 py-2 text-ink-600">{u.email}</td>
                <td className="px-4 py-2">
                  <span className={`badge ${
                    u.role === 'admin' ? 'bg-brand-100 text-brand-700'
                    : u.role === 'supervisor' ? 'bg-amber-100 text-amber-700'
                    : 'bg-ink-100 text-ink-700'
                  }`}>
                    {u.role}
                  </span>
                </td>
                <td className="px-4 py-2">
                  <span className={`badge ${u.active ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                    {u.active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-4 py-2 text-ink-600">{u.voiplineExtension || '—'}</td>
                <td className="px-4 py-2 text-xs text-ink-600">
                  {u.sipUser
                    ? <>{u.sipUser}{u.sipPasswordSet ? '' : <span className="text-amber-600"> · no pwd</span>}</>
                    : <span className="text-ink-400">shared trunk</span>}
                </td>
                <td className="px-4 py-2 text-xs text-ink-500">{u.skills.join(', ') || '—'}</td>
                <td className="px-4 py-2">
                  <div className="flex gap-2">
                    <button className="btn-ghost text-xs" onClick={() => openEdit(u)}>Edit</button>
                    <button
                      className={`btn-ghost text-xs ${u.active ? 'text-amber-600' : 'text-emerald-600'}`}
                      onClick={() => toggleActive(u)}
                    >
                      {u.active ? 'Deactivate' : 'Reactivate'}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}

      {/* Create modal */}
      {modal?.type === 'create' && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-ink-900/40 p-4">
          <div className="card w-full max-w-md p-4 space-y-3">
            <h3 className="text-sm font-semibold text-ink-900">Add user</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label">First name</label>
                <input className="input" value={createForm.firstName}
                  onChange={(e) => setCreateForm((f) => ({ ...f, firstName: e.target.value }))} />
              </div>
              <div>
                <label className="label">Last name</label>
                <input className="input" value={createForm.lastName}
                  onChange={(e) => setCreateForm((f) => ({ ...f, lastName: e.target.value }))} />
              </div>
            </div>
            <div>
              <label className="label">Email</label>
              <input className="input" type="email" value={createForm.email}
                onChange={(e) => setCreateForm((f) => ({ ...f, email: e.target.value }))} />
            </div>
            <div>
              <label className="label">Password</label>
              <input className="input" type="password" value={createForm.password}
                onChange={(e) => setCreateForm((f) => ({ ...f, password: e.target.value }))} />
            </div>
            <div>
              <label className="label">Role</label>
              <select className="input" value={createForm.role}
                onChange={(e) => setCreateForm((f) => ({ ...f, role: e.target.value as UserRow['role'] }))}>
                {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <label className="label">VP Extension</label>
              <input className="input" type="text" value={createForm.voiplineExtension} placeholder="e.g. 1001"
                onChange={(e) => setCreateForm((f) => ({ ...f, voiplineExtension: e.target.value }))} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label">SIP User <span className="normal-case text-ink-400">(optional)</span></label>
                <input className="input" type="text" value={createForm.sipUser} placeholder="leave blank to use shared trunk"
                  onChange={(e) => setCreateForm((f) => ({ ...f, sipUser: e.target.value }))} />
              </div>
              <div>
                <label className="label">SIP Password</label>
                <input className="input" type="password" value={createForm.sipPassword} placeholder="••••••••"
                  onChange={(e) => setCreateForm((f) => ({ ...f, sipPassword: e.target.value }))} />
              </div>
            </div>
            {errorMsg && <p className="text-sm text-red-600">{errorMsg}</p>}
            <div className="flex justify-end gap-2 pt-1">
              <button className="btn-secondary" onClick={closeModal}>Cancel</button>
              <button className="btn-primary" disabled={createUser.isPending}
                onClick={() => createUser.mutate(createForm)}>
                {createUser.isPending ? 'Creating…' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit modal */}
      {modal?.type === 'edit' && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-ink-900/40 p-4">
          <div className="card w-full max-w-md p-4 space-y-3">
            <h3 className="text-sm font-semibold text-ink-900">Edit user</h3>
            <p className="text-xs text-ink-500">{modal.user.email}</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label">First name</label>
                <input className="input" value={editForm.firstName}
                  onChange={(e) => setEditForm((f) => ({ ...f, firstName: e.target.value }))} />
              </div>
              <div>
                <label className="label">Last name</label>
                <input className="input" value={editForm.lastName}
                  onChange={(e) => setEditForm((f) => ({ ...f, lastName: e.target.value }))} />
              </div>
            </div>
            <div>
              <label className="label">Role</label>
              <select className="input" value={editForm.role}
                onChange={(e) => setEditForm((f) => ({ ...f, role: e.target.value as UserRow['role'] }))}>
                {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <label className="label">VP Extension</label>
              <input className="input" type="text" value={editForm.voiplineExtension} placeholder="e.g. 1001"
                onChange={(e) => setEditForm((f) => ({ ...f, voiplineExtension: e.target.value }))} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label">SIP User <span className="normal-case text-ink-400">(optional)</span></label>
                <input className="input" type="text" value={editForm.sipUser} placeholder="leave blank to use shared trunk"
                  onChange={(e) => setEditForm((f) => ({ ...f, sipUser: e.target.value }))} />
              </div>
              <div>
                <label className="label">SIP Password <span className="normal-case text-ink-400">(blank keeps current)</span></label>
                <input className="input" type="password" value={editForm.sipPassword} placeholder="••••••••"
                  onChange={(e) => setEditForm((f) => ({ ...f, sipPassword: e.target.value }))} />
              </div>
            </div>
            <div>
              <label className="label">New password <span className="normal-case text-ink-400">(leave blank to keep current)</span></label>
              <input className="input" type="password" value={editForm.password} placeholder="••••••••"
                onChange={(e) => setEditForm((f) => ({ ...f, password: e.target.value }))} />
            </div>
            <label className="flex items-center gap-2 text-sm text-ink-700 cursor-pointer">
              <input type="checkbox" checked={editForm.active}
                onChange={(e) => setEditForm((f) => ({ ...f, active: e.target.checked }))} />
              Active
            </label>
            {errorMsg && <p className="text-sm text-red-600">{errorMsg}</p>}
            <div className="flex justify-end gap-2 pt-1">
              <button className="btn-secondary" onClick={closeModal}>Cancel</button>
              <button className="btn-primary" disabled={editUser.isPending} onClick={submitEdit}>
                {editUser.isPending ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
